import type { OptionsMap } from '@openx/types/options.ts';
import type {
  RetargetingDisposition,
  RetargetingFormState,
  RetargetingSegmentApiParams,
  RetargetingSegmentOption,
} from '@openx/types/targeting/retargeting.ts';
import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import { noop } from '@openx/utils/noop.ts';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { EmptyFilterMessage } from '../shared/EmptyFilterMessage/EmptyFilterMessage.tsx';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer.tsx';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer.tsx';
import { Items } from './Items.tsx';
import { RetargetingReadOnly } from './RetargetingReadOnly.tsx';
import { TopBox } from './TopBox.tsx';
import {
  emptyRetargetingFormState,
  fromRetargetingSegmentApiParams,
  toRetargetingSegmentApiParams,
} from './utils/retargetingFormState.ts';

export const Retargeting = ({
  targetingParams,
  onFieldUpdate = noop,
  name,
  accountUid,
  readonly,
  field,
}: TargetingProps<RetargetingSegmentApiParams>) => {
  const { t } = useTranslation();
  const { useRetargetingSegmentsOptionsFetch = noop, useRetargetingSegmentDetailsFetch = noop } = useTargetingContext();
  const [userChangedInitialValue, setUserChangedInitialValue] = useState(false);

  const initState = useMemo(
    () => fromRetargetingSegmentApiParams(targetingParams ? cloneDeep(targetingParams) : null),
    [targetingParams]
  );

  const [params, updateParams] = useState<RetargetingFormState>(initState);

  useEffect(() => {
    updateParams(initState);
  }, [initState]);

  const { segment: resolvedSegment, isLoading: isSegmentsLoading } = useRetargetingSegmentDetailsFetch({
    accountUid,
    segmentId: initState.segment_id,
    enabled: Boolean(initState.segment_id),
  }) || { segment: null, isLoading: false };

  const isDataUpdated = useMemo(() => !isEqual(initState, params), [initState, params]);
  const isEmpty = !params.segment_id;

  const onFieldUpdateExtended = useCallback(
    (...args: Parameters<typeof onFieldUpdate>) => {
      setUserChangedInitialValue(true);
      onFieldUpdate(...args);
    },
    [onFieldUpdate]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdateExtended(field, toRetargetingSegmentApiParams(params)),
    restoreParams: () => updateParams({ ...initState }),
  });

  const { segments } = useRetargetingSegmentsOptionsFetch({
    accountUid,
    disposition: params.disposition,
    enabled: isOpen && Boolean(params.disposition),
  }) || { segments: {} as Record<string, RetargetingSegmentOption> };

  const segmentOptions = useMemo<OptionsMap>(() => {
    const map: OptionsMap = {};

    for (const [id, option] of Object.entries(segments)) {
      map[id] = { id, name: option.name };
    }

    if (resolvedSegment) {
      map[resolvedSegment.id] = { id: resolvedSegment.id, name: resolvedSegment.name };
    }

    if (params.segment_id && segments[params.segment_id]) {
      map[params.segment_id] = {
        id: params.segment_id,
        name: segments[params.segment_id].name,
      };
    }

    return map;
  }, [params.segment_id, segments, resolvedSegment]);

  const onRemoveAll = useCallback(() => {
    onFieldUpdateExtended(field, null);
    updateParams(emptyRetargetingFormState());
  }, [field, onFieldUpdateExtended]);

  const onClear = useCallback(() => {
    updateParams(emptyRetargetingFormState());
  }, []);

  const onDesignationChange = useCallback((disposition: RetargetingDisposition | '') => {
    updateParams(() => ({
      ...emptyRetargetingFormState(),
      disposition,
    }));
  }, []);

  const onSegmentChange = useCallback(
    (segmentId: string | null) => {
      if (!segmentId) {
        updateParams(emptyRetargetingFormState());
        return;
      }

      if (!params.disposition) {
        return;
      }

      const selected = segments[segmentId];

      if (!selected) {
        return;
      }

      updateParams({
        disposition: params.disposition,
        segment_id: selected.id,
      });
    },
    [params.disposition, segments]
  );

  const appliedTargeting = useMemo(() => toRetargetingSegmentApiParams(initState), [initState]);

  return (
    <>
      <RetargetingReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        targetingParams={appliedTargeting}
        formState={params}
        onOpenDrawerClick={onDrawerOpen}
        onRemoveClick={onRemoveAll}
        readonly={readonly}
        onClear={onClear}
        isSegmentsLoading={isSegmentsLoading}
        segmentOptions={segmentOptions}
      />

      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <TopBox
            accountUid={accountUid}
            params={params}
            onDesignationChange={onDesignationChange}
            onSegmentChange={onSegmentChange}
          />

          {isEmpty ? (
            <EmptyFilterMessage text={t('No filters applied.')} />
          ) : (
            <Items
              targetingParams={params}
              readonly={false}
              onClear={onClear}
              isLoading={isSegmentsLoading}
              options={segmentOptions}
            />
          )}
        </TargetingDrawer>
      )}
    </>
  );
};
